import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import {
  getTagChipSx,
  removeJobTag,
  resolveTagSelection,
  upsertTagOption,
} from '../../../utils/jobTagSuggestions';
import { getModernTextFieldSx } from './modernFormFieldStyles';

const filter = createFilterOptions();

export default function JobTagsField({
  value = [],
  onChange,
  tagOptions = [],
  setTagOptions,
  canCreateTags = false,
  canDeleteTags = false,
  readOnly = false,
  size = 'medium',
  placeholder = 'Select tags',
  label = 'Tags',
  sx,
}) {
  const theme = useTheme();
  const modernFieldSx = getModernTextFieldSx(theme.palette.mode === 'dark');

  if (readOnly) {
    if (!value?.length) {
      return (
        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: size === 'small' ? '1rem' : '0.95rem' }}>
          None
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {value.map((tag) => (
          <Chip
            key={tag.id}
            label={tag.name}
            size="small"
            sx={getTagChipSx(tag, theme)}
          />
        ))}
      </Box>
    );
  }

  const handleChange = async (_event, newValue) => {
    const resolved = await resolveTagSelection(newValue, canCreateTags);
    onChange(resolved);

    if (canCreateTags && setTagOptions) {
      const createdEntries = resolved.filter(
        (tag) => !tagOptions.some((option) => option.id === tag.id),
      );
      if (createdEntries.length > 0) {
        setTagOptions((prev) => createdEntries.reduce(
          (next, entry) => upsertTagOption(next, entry),
          prev,
        ));
      }
    }
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={tagOptions}
      value={value}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        const input = params.inputValue.trim();
        if (
          canCreateTags
          && input
          && !options.some((option) => option.name.toLowerCase() === input.toLowerCase())
        ) {
          filtered.push({
            inputValue: input,
            name: `Add "${input}"`,
          });
        }
        return filtered;
      }}
      onChange={handleChange}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((tag, index) => (
          <Chip
            {...getTagProps({ index })}
            key={tag.id}
            label={tag.name}
            size="small"
            sx={getTagChipSx(tag, theme)}
          />
        ))
      }
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const name = option.inputValue ? option.name : option.name;
        const optionId = option.inputValue ? null : option.id;

        return (
          <li
            key={key}
            {...optionProps}
            style={{
              ...optionProps.style,
              padding: size === 'small' ? '8px 12px' : '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant={size === 'small' ? 'body2' : 'body1'} sx={{ fontWeight: 500 }}>
              {name}
            </Typography>
            {canDeleteTags && optionId && (
              <IconButton
                size="small"
                onClick={async (e) => {
                  e.stopPropagation();
                  const removed = await removeJobTag(optionId);
                  if (removed) {
                    setTagOptions((prev) => prev.filter((item) => item.id !== optionId));
                    onChange(value.filter((item) => item.id !== optionId));
                  }
                }}
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                aria-label={`Remove "${option.name}" tag`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size={size}
          sx={modernFieldSx}
        />
      )}
      sx={sx ? { ...modernFieldSx, ...sx } : modernFieldSx}
      fullWidth
    />
  );
}
